import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";
import type { components } from "../../api/schema.gen";
import { PORTFOLIO_KEY } from "./usePortfolio";

export type PositionCreate = components["schemas"]["PositionCreateDTO"];
export type TradeCreate = components["schemas"]["TradeCreateDTO"];

// Создание новой позиции. После успеха — инвалидация кеша портфеля,
// чтобы UI сразу перезапросил список и пересчитал KPI.
export function useCreatePosition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: PositionCreate) => {
      const { data, error } = await api.POST("/api/positions", { body });
      if (error || !data) throw new Error(String(error ?? "create failed"));
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PORTFOLIO_KEY });
    },
  });
}

export function useDeletePosition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (positionId: number) => {
      const { error } = await api.DELETE("/api/positions/{position_id}", {
        params: { path: { position_id: positionId } },
      });
      if (error) throw new Error(String(error));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PORTFOLIO_KEY });
    },
  });
}

// Запись сделки (sell/coupon/redeem/amort). Сервер сам списывает quantity
// для sell-like типов и пересчитывает realised PnL.
export function useRecordTrade() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { positionId: number; trade: TradeCreate }) => {
      const { data, error } = await api.POST(
        "/api/positions/{position_id}/trades",
        {
          params: { path: { position_id: args.positionId } },
          body: args.trade,
        },
      );
      if (error || !data) throw new Error(String(error ?? "record failed"));
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PORTFOLIO_KEY });
    },
  });
}
